import { Component, forwardRef, inject, input, signal, computed, OnInit, OnDestroy, ElementRef, output } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { TextComponent } from '../../typography/text/text.component';
import { InputComponent } from '../../forms/input/input.component';
import { PartnerService, Partner, PartnerType } from '../../../../core/services/partner.service';
import { Subscription } from 'rxjs';

export interface PartnerReference {
  id: string; // companyRelationships doc ID
  partnerCompanyId: string; // companyDirectory doc ID
  partnerName: string; // Legal name
  legalName?: string;
  dbaName?: string | null; // DBA / "doing business as" name
  dotNumber?: string;
  mcNumber?: string;
  partnerTypes?: PartnerType[];
}

@Component({
  selector: 'app-partner-search',
  standalone: true,
  imports: [
    FormsModule,
    LucideIconComponent,
    TextComponent,
    InputComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PartnerSearchComponent),
      multi: true,
    },
  ],
  styles: [`
    .dropdown-item {
      transition: background-color 150ms ease, transform 100ms ease;
    }
    .dropdown-item:hover {
      background-color: hsl(var(--color-primary-low-contrast));
    }
    .dropdown-item:hover .item-icon,
    .dropdown-item:hover .item-name {
      color: hsl(var(--color-primary));
    }
    .dropdown-item:active {
      transform: scale(0.995);
    }
    .add-new-btn {
      transition: background-color 150ms ease, transform 100ms ease;
    }
    .add-new-btn:hover {
      background-color: hsl(var(--color-primary-low-contrast));
    }
    .add-new-btn:hover .add-icon-circle {
      background-color: hsl(var(--color-primary));
      color: white;
    }
    .add-new-btn:active {
      transform: scale(0.995);
    }
  `],
  template: `
    <div class="relative">
      <!-- Selected Partner Display -->
      @if (selectedPartner() && !isOpen()) {
      <div class="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-surface cursor-pointer hover:border-primary transition-all duration-150"
           (click)="openDropdown()"
           (keydown.enter)="openDropdown()"
           (keydown.space)="openDropdown()"
           tabindex="0"
           role="button">
        <lucide-icon name="building" class="w-4 h-4 text-muted shrink-0"></lucide-icon>
        <div class="flex-1 min-w-0">
          <app-text size="sm" weight="medium" class="block truncate">
            {{ selectedPartner()?.dbaName || selectedPartner()?.partnerName || selectedPartner()?.legalName }}
          </app-text>
          @if (selectedPartner()?.dbaName && (selectedPartner()?.partnerName || selectedPartner()?.legalName)) {
          <app-text size="xs" variant="muted" class="block truncate">
            {{ selectedPartner()?.partnerName || selectedPartner()?.legalName }}
          </app-text>
          }
        </div>
        @if (selectedPartner()?.dotNumber) {
        <app-text size="xs" variant="muted">DOT: {{ selectedPartner()?.dotNumber }}</app-text>
        }
        <button type="button" class="p-1 rounded-md hover:bg-surface-high cursor-pointer transition-colors" (click)="clearSelection($event)">
          <lucide-icon name="x" class="w-3.5 h-3.5 text-muted"></lucide-icon>
        </button>
      </div>
      } @else {
      <!-- Search Input -->
      <app-input
        [ngModel]="searchText()"
        (ngModelChange)="onSearchInput($event)"
        (inputFocus)="openDropdown()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        prefixIcon="search"
        autocomplete="off">
      </app-input>
      }

      <!-- Dropdown -->
      @if (isOpen()) {
      <div class="absolute z-50 w-full mt-1 rounded-lg border border-border shadow-xl overflow-hidden bg-background"
           style="box-shadow: 0 8px 30px rgba(0,0,0,0.25);">
        <div class="max-h-[240px] overflow-y-auto">
          @if (filteredPartners().length === 0 && searchText()) {
          <div class="px-4 py-4 text-center">
            <app-text size="sm" variant="muted">
              No matching partners found
            </app-text>
          </div>
          } @else if (filteredPartners().length === 0) {
          <div class="px-4 py-4 text-center">
            <app-text size="sm" variant="muted">
              No partners available
            </app-text>
          </div>
          }
          @for (partner of filteredPartners(); track partner.id) {
          <button
            type="button"
            class="dropdown-item w-full px-4 py-2.5 text-left cursor-pointer flex items-center gap-3 border-b border-border last:border-b-0"
            (mousedown)="selectPartner(partner)">
            <lucide-icon name="building" class="item-icon w-4 h-4 text-muted shrink-0 transition-colors"></lucide-icon>
            <div class="flex-1 min-w-0">
              <app-text size="sm" weight="medium" class="item-name block truncate transition-colors">
                {{ partner.dbaName || partner.partnerName }}
              </app-text>
              <app-text size="xs" variant="muted" class="block truncate">
                @if (partner.dbaName && partner.partnerName) {
                  {{ partner.partnerName }}
                  @if (partner.partnerDotNumber || partner.dotNumber) {
                    · DOT: {{ partner.partnerDotNumber || partner.dotNumber }}
                  }
                } @else {
                  @if (partner.partnerDotNumber || partner.dotNumber) {
                    DOT: {{ partner.partnerDotNumber || partner.dotNumber }}
                  }
                  @if (partner.partnerMcNumber || partner.mcNumber) {
                    · MC: {{ partner.partnerMcNumber || partner.mcNumber }}
                  }
                }
              </app-text>
            </div>
            <div class="flex gap-1">
              @for (type of (partner.partnerTypes || []); track type) {
              <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border border-border bg-surface text-muted">
                {{ type }}
              </span>
              }
            </div>
          </button>
          }
        </div>

        <!-- Add New Partner — always visible at dropdown bottom -->
        @if (showAddNew()) {
        <button
          type="button"
          class="add-new-btn w-full px-4 py-3 text-left cursor-pointer border-t border-border bg-surface-high flex items-center gap-3"
          (mousedown)="onAddNewClick()">
          <div class="add-icon-circle flex items-center justify-center w-6 h-6 rounded-full bg-primary-low-contrast text-primary transition-colors duration-150">
            <lucide-icon name="plus" class="w-3.5 h-3.5"></lucide-icon>
          </div>
          <div>
            <app-text size="sm" weight="medium" class="text-primary block">
              Add a new partner
            </app-text>
            <app-text size="xs" variant="muted">
              Search DOT registry and add to your network
            </app-text>
          </div>
        </button>
        }
      </div>
      }
    </div>
  `,
  host: { class: 'block relative' },
})
export class PartnerSearchComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly partnerService = inject(PartnerService);
  private readonly elRef = inject(ElementRef);

  /** Filter partners by type (e.g., show only brokers in broker field) */
  filterByType = input<PartnerType | null>(null);
  placeholder = input<string>('Search partners...');

  /** Whether to show the "Add new partner" option at the bottom of the dropdown */
  showAddNew = input<boolean>(true);

  /** Emitted when user clicks "Add a new partner" inside the dropdown */
  addNewPartner = output<void>();

  // State
  searchText = signal('');
  isOpen = signal(false);
  disabled = signal(false);
  selectedPartner = signal<PartnerReference | null>(null);
  allPartners = signal<Partner[]>([]);

  private sub?: Subscription;
  private clickListener?: (event: Event) => void;

  // CVA
  onChange: (value: PartnerReference | null) => void = () => {};
  onTouched: () => void = () => {};

  /** Filtered partners based on search text and type filter */
  filteredPartners = computed(() => {
    const partners = this.allPartners();
    const filterType = this.filterByType();
    const search = this.searchText().toLowerCase().trim();

    let filtered = partners;

    // Filter by partner type if specified
    if (filterType) {
      filtered = filtered.filter(p => {
        const types = p.partnerTypes || (p.partnerType ? [p.partnerType] : []);
        return types.includes(filterType);
      });
    }

    // Exclude factoring companies
    filtered = filtered.filter(p => {
      const types = p.partnerTypes || (p.partnerType ? [p.partnerType] : []);
      return !(types.length === 1 && types[0] === 'factoring');
    });

    // Filter by search text
    if (search) {
      filtered = filtered.filter(p =>
        (p.partnerName?.toLowerCase().includes(search)) ||
        (p.dbaName?.toLowerCase()?.includes(search)) ||
        (p.dotNumber?.includes(search)) ||
        (p.mcNumber?.includes(search)) ||
        (p.partnerDotNumber?.includes(search)) ||
        (p.partnerMcNumber?.includes(search)) ||
        (p.address?.toLowerCase()?.includes(search))
      );
    }

    // Sort alphabetically by display name (DBA preferred, fallback to legal)
    return filtered.sort((a, b) =>
      (a.dbaName || a.partnerName || '').localeCompare(b.dbaName || b.partnerName || '')
    );
  });

  ngOnInit() {
    this.loadPartners();

    // Close dropdown when clicking outside this component
    this.clickListener = (event: Event) => {
      if (!this.elRef.nativeElement.contains(event.target)) {
        this.isOpen.set(false);
      }
    };
    document.addEventListener('click', this.clickListener);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
  }

  private loadPartners() {
    this.sub = this.partnerService.getPartnersForDropdown().subscribe(partners => {
      this.allPartners.set(partners);
    });
  }

  /** Reload partners from service — called after a new partner is added */
  refreshPartners() {
    this.sub?.unsubscribe();
    this.loadPartners();
  }

  openDropdown() {
    if (!this.disabled()) {
      this.isOpen.set(true);
    }
  }

  onSearchInput(value: string) {
    this.searchText.set(value);
    if (!this.isOpen()) {
      this.isOpen.set(true);
    }
  }

  onAddNewClick() {
    this.isOpen.set(false);
    this.addNewPartner.emit();
  }

  selectPartner(partner: Partner) {
    const ref: PartnerReference = {
      id: partner.id || '',
      partnerCompanyId: partner.partnerCompanyId || '',
      partnerName: partner.partnerName || '',
      legalName: partner.partnerName || '',
      dbaName: partner.dbaName || null,
      dotNumber: partner.partnerDotNumber || partner.dotNumber,
      mcNumber: partner.partnerMcNumber || partner.mcNumber,
      partnerTypes: partner.partnerTypes || (partner.partnerType ? [partner.partnerType] : []),
    };
    this.selectedPartner.set(ref);
    this.searchText.set('');
    this.isOpen.set(false);
    this.onChange(ref);
    this.onTouched();
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.selectedPartner.set(null);
    this.searchText.set('');
    this.onChange(null);
  }

  // CVA implementation
  writeValue(value: any): void {
    if (value && !value.partnerName && value.legalName) {
      this.selectedPartner.set({
        ...value,
        partnerName: value.legalName,
      });
    } else {
      this.selectedPartner.set(value);
    }
  }

  registerOnChange(fn: (value: PartnerReference | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
